import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import type { Trade } from '@/mocks/technical';
import { SAMPLE_TRADES, STRATEGY_USE_CASES } from '@/mocks/technical';
import { computeKpis } from '../calculations';
import type { StudioApi } from '../useTechnicalStudio';
import { fmtUsd, SectionHeading, StatCard, Tag } from './ui';
import { EquityChart, DistributionChart, DrawdownChart } from './strategy/Charts';
import {
  DirectionComparison,
  DistributionStats,
  DrawdownAnalysis,
  HealthGrid,
  PeriodComparison,
  TimeAnalysis,
} from './strategy/AnalysisGrid';
import { parseTradeRows } from './strategy/parse';

type TimeRange = 'week' | 'month' | 'all';

function filterByRange(trades: Trade[], range: TimeRange): Trade[] {
  if (range === 'all') return trades;
  const sorted = [...trades].sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length === 0) return [];
  const last = new Date(sorted[sorted.length - 1].date).getTime();
  const days = range === 'week' ? 7 : 30;
  const cutoff = last - days * 86400000;
  return sorted.filter((t) => new Date(t.date).getTime() >= cutoff);
}

export default function StrategySection({ api }: { api: StudioApi }) {
  const { trades, setTrades } = api;
  const fileRef = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState<'sample' | 'upload' | 'manual'>('sample');
  const [fileName, setFileName] = useState('Dữ liệu mẫu');
  const [error, setError] = useState('');
  const [equityMode, setEquityMode] = useState<'cumulative' | 'balance'>('cumulative');
  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [manual, setManual] = useState({ date: '2025-03-28', dir: 'long', lot: 0.5, pnl: 20 });

  const kpis = computeKpis(trades);
  const filtered = filterByRange(trades, timeRange);

  const loadSample = () => {
    setTrades(SAMPLE_TRADES);
    setSource('sample');
    setFileName('Dữ liệu mẫu');
    setError('');
  };

  const handleFile = async (file: File) => {
    setError('');
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
      const parsed = parseTradeRows(rows);
      if (parsed.length === 0) {
        setError('Không tìm thấy giao dịch nào trong tệp. Kiểm tra lại cột Date và Profit/Loss.');
        return;
      }
      setTrades(parsed);
      setSource('upload');
      setFileName(file.name);
    } catch {
      setError('Không thể đọc tệp. Vui lòng thử tệp CSV hoặc Excel khác.');
    }
  };

  const addManual = () => {
    const t: Trade = {
      date: manual.date,
      dir: manual.dir as 'long' | 'short',
      symbol: 'MANUAL',
      lot: manual.lot,
      pnl: manual.pnl,
      session: undefined,
    };
    setTrades([...trades, t]);
    setSource('manual');
    setFileName('Dữ liệu thủ công');
  };

  return (
    <section id="strategy" className="scroll-mt-24">
      <SectionHeading
        eyebrow="Công cụ 3"
        title="Strategy Data Analyzer"
        subtitle="Phân tích dữ liệu giao dịch để xem các chỉ số hiệu suất, phân bố kết quả và thay đổi của tài khoản theo thời gian."
      />

      {/* Data input */}
      <div className="mt-6 rounded-3xl border border-slate-200/80 bg-white p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="font-display text-base font-bold text-slate-900">Nhập dữ liệu giao dịch</h3>
            <p className="mt-1 text-xs text-slate-400">Tải lên CSV / Excel, nhập thủ công, hoặc dùng dữ liệu mẫu.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="whitespace-nowrap rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
            >
              <i className="ri-upload-2-line mr-1.5" />
              Tải tệp lên
            </button>
            <button
              type="button"
              onClick={loadSample}
              className={`whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                source === 'sample' ? 'border-violet-300 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              Dùng dữ liệu mẫu
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        {/* Manual entry */}
        <div className="mt-4 grid grid-cols-2 gap-3 rounded-2xl border border-slate-200/80 bg-slate-50/50 p-4 md:grid-cols-5">
          <div>
            <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">Ngày</span>
            <input
              type="date"
              value={manual.date}
              onChange={(e) => setManual({ ...manual, date: e.target.value })}
              className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-medium text-slate-700 outline-none focus:border-violet-400"
            />
          </div>
          <div>
            <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">Hướng</span>
            <select
              value={manual.dir}
              onChange={(e) => setManual({ ...manual, dir: e.target.value })}
              className="w-full cursor-pointer rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-medium text-slate-700 outline-none focus:border-violet-400"
            >
              <option value="long">Long (Mua)</option>
              <option value="short">Short (Bán)</option>
            </select>
          </div>
          <div>
            <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">Lot</span>
            <input
              type="number"
              step={0.01}
              value={manual.lot}
              onChange={(e) => setManual({ ...manual, lot: parseFloat(e.target.value) || 0 })}
              className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 font-mono text-xs text-slate-700 outline-none focus:border-violet-400"
            />
          </div>
          <div>
            <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">Lãi / Lỗ (USD)</span>
            <input
              type="number"
              step={1}
              value={manual.pnl}
              onChange={(e) => setManual({ ...manual, pnl: parseFloat(e.target.value) || 0 })}
              className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 font-mono text-xs text-slate-700 outline-none focus:border-violet-400"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={addManual}
              className="w-full whitespace-nowrap rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
            >
              <i className="ri-add-line mr-1" />
              Thêm
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-400">Nguồn dữ liệu:</span>
          {source === 'sample' ? <Tag tone="emerald">Dữ liệu mẫu</Tag> : <Tag tone="violet">{fileName}</Tag>}
          <span className="font-mono text-xs text-slate-500">{trades.length} giao dịch</span>
        </div>

        {error && (
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-600">
            <i className="ri-error-warning-line" />
            {error}
          </div>
        )}
      </div>

      {/* KPI dashboard */}
      <div className="mt-6">
        <h3 className="font-display text-base font-bold text-slate-900">Tổng quan hiệu suất</h3>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Tổng số giao dịch" value={`${kpis.total}`} />
          <StatCard label="Tỷ lệ thắng" value={`${kpis.winRate.toFixed(1)}%`} />
          <StatCard label="Lợi nhuận ròng" value={fmtUsd(kpis.netProfit)} tone={kpis.netProfit >= 0 ? 'positive' : 'negative'} />
          <StatCard label="Profit Factor" value={kpis.profitFactor === Infinity ? '∞' : kpis.profitFactor.toFixed(2)} />
          <StatCard label="Max Drawdown" value={`-${kpis.maxDrawdown.toFixed(1)}%`} tone="negative" />
          <StatCard label="Avg. Trade" value={fmtUsd(kpis.avgTrade)} tone={kpis.avgTrade >= 0 ? 'positive' : 'negative'} />
        </div>
      </div>

      {/* Equity curve */}
      <div className="mt-6 rounded-3xl border border-slate-200/80 bg-white p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-display text-base font-bold text-slate-900">Đường cong vốn</h3>
          <div className="flex flex-wrap gap-2">
            <div className="flex rounded-full bg-slate-100 p-1">
              {(['cumulative', 'balance'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setEquityMode(m)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    equityMode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {m === 'cumulative' ? 'Cumulative Profit' : 'Account Balance'}
                </button>
              ))}
            </div>
            <div className="flex rounded-full bg-slate-100 p-1">
              {(['week', 'month', 'all'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setTimeRange(r)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    timeRange === r ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {r === 'week' ? 'Week' : r === 'month' ? 'Month' : 'All'}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <EquityChart trades={filtered} mode={equityMode} />
        </div>
      </div>

      {/* Distribution */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6">
          <h3 className="font-display text-base font-bold text-slate-900">Phân bố kết quả giao dịch</h3>
          <p className="mt-1 text-xs text-slate-400">Số lệnh thắng và thua, cùng quy mô trung bình và lớn nhất.</p>
          <div className="mt-4">
            <DistributionChart trades={trades} />
          </div>
          <div className="mt-4">
            <DistributionStats trades={trades} />
          </div>
        </div>

        {/* Strategy health */}
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6">
          <h3 className="font-display text-base font-bold text-slate-900">Tổng quan sức khỏe chiến lược</h3>
          <p className="mt-1 text-xs text-slate-400">Các chỉ số khách quan, không đánh giá chủ quan.</p>
          <div className="mt-4">
            <HealthGrid kpis={kpis} />
          </div>
        </div>
      </div>

      {/* Time analysis */}
      <div className="mt-6 rounded-3xl border border-slate-200/80 bg-white p-6">
        <h3 className="font-display text-base font-bold text-slate-900">Hiệu suất theo thời gian</h3>
        <p className="mt-1 text-xs text-slate-400">Phân tích theo ngày trong tuần và phiên giao dịch.</p>
        <div className="mt-4">
          <TimeAnalysis trades={trades} />
        </div>
      </div>

      {/* Long vs Short */}
      <div className="mt-6">
        <h3 className="font-display text-base font-bold text-slate-900">Phân tích theo hướng giao dịch</h3>
        <p className="mt-1 text-xs text-slate-400">So sánh khách quan dữ liệu lịch sử Buy / Sell.</p>
        <div className="mt-4">
          <DirectionComparison trades={trades} />
        </div>
      </div>

      {/* Drawdown */}
      <div className="mt-6 rounded-3xl border border-slate-200/80 bg-white p-6">
        <h3 className="font-display text-base font-bold text-slate-900">Phân tích Drawdown</h3>
        <p className="mt-1 text-xs text-slate-400">Các chỉ số sụt giảm vốn lịch sử.</p>
        <div className="mt-4">
          <DrawdownAnalysis trades={trades} />
        </div>
        <div className="mt-4">
          <DrawdownChart trades={trades} />
        </div>
      </div>

      {/* Period comparison */}
      <div className="mt-6 rounded-3xl border border-slate-200/80 bg-white p-6">
        <h3 className="font-display text-base font-bold text-slate-900">So sánh hiệu suất</h3>
        <p className="mt-1 text-xs text-slate-400">So sánh hai giai đoạn bằng nhau của dữ liệu.</p>
        <div className="mt-4">
          <PeriodComparison trades={trades} />
        </div>
      </div>

      {/* Use cases */}
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        {STRATEGY_USE_CASES.map((u) => (
          <div key={u.title} className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <i className={u.icon} />
            </span>
            <h4 className="mt-3 text-sm font-bold text-slate-800">{u.title}</h4>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{u.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}